import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  PLATFORM_ID,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import {
  NoGrafoResponse,
  VisaoGrafoResponse,
} from '@interfaces/corporate-registry/responses/rede-societaria.response';
import { injectOwnershipNetworkDisplay } from '@shared/components/ownership-network/ownership-network-display';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { VaultMaskCnpjPipe } from '@shared/pipes/vault-mask-cnpj.pipe';
import { truncarRotulo } from '@shared/components/ownership-network/registry-formatters';
import {
  ajustarAoQuadro,
  corDoNo,
  CORES_GRAFO,
  emAlerta,
  pontaDaSeta,
  raioDoNo,
  Transformacao,
  zoomEm,
} from '@shared/components/ownership-network/registry-graph-geometry';

interface NoTela {
  no: NoGrafoResponse;
  sx: number;
  sy: number;
  r: number;
  cor: string;
  alerta: boolean;
  rotulo: string;
  larguraRotulo: number;
}

interface ArestaTela {
  chave: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  seta: string | null;
}

/** Fonte do rótulo do nó no Dossiê: `500 11px Montserrat`. */
const FONTE_ROTULO = 11;

/**
 * Mapa societário em SVG — réplica do `GrafoRede` do Vault-Dossiê (react-force-graph-2d).
 *
 * O que foi copiado de lá, medido no código e nos prints do card 2988:
 * - nós com raio FIXO na tela (12 raiz · 9 empresa · 8 pessoa), contorno branco de 2px e anel
 *   vermelho (+2.5px) em empresa com situação ≠ 02;
 * - rótulo `500 11px Montserrat`, cortado em 29 + "...", com fundo `rgba(252,253,255,.88)`;
 * - arestas `#c9d6ee` de 1.2px, com seta a 92% só quando o layout vem posicionado do servidor;
 * - `zoomToFit` com 70px de margem; roda do mouse = zoom no cursor; arrastar o fundo = mover;
 * - passar o mouse mostra o popover (e o tooltip escuro com o nome inteiro); clicar fixa;
 *   clicar no fundo, dar zoom ou apertar Esc fecha.
 *
 * Sem biblioteca de grafo de propósito: o projeto não tem nenhuma, e com as posições prontas
 * (ou calculadas por `layoutDeForca`) o desenho é só círculo, linha e texto.
 */
@Component({
  selector: 'app-registry-graph',
  standalone: true,
  imports: [CommonModule, VaultMaskCnpjPipe, VaultDatePipe],
  templateUrl: './registry-graph.component.html',
  styleUrl: './registry-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistryGraphComponent {
  readonly grafo = input.required<VisaoGrafoResponse>();
  /** Altura da área, como o `altura` do `GrafoRede` (560 no card; 100% em tela cheia). */
  readonly altura = input<string>('558px');

  private readonly host = viewChild<ElementRef<HTMLDivElement>>('area');
  private readonly destroyRef = inject(DestroyRef);
  private readonly display = injectOwnershipNetworkDisplay();
  private readonly noNavegador = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly cores = CORES_GRAFO;
  protected readonly fonte = FONTE_ROTULO;

  private readonly largura = signal(0);
  private readonly alturaPx = signal(0);
  protected readonly transf = signal<Transformacao>({ k: 1, tx: 0, ty: 0 });

  /** Nó sob o mouse (tooltip escuro). */
  protected readonly emFoco = signal<string | null>(null);
  /** Popover: aberto no hover, fixado no clique. */
  protected readonly selecao = signal<{ id: string; fixado: boolean } | null>(null);

  private arrasto: { x: number; y: number; t: Transformacao; moveu: boolean } | null = null;
  /** Nó sendo arrastado (o `enableNodeDrag` do react-force-graph do Dossiê). */
  private arrastoNo: { id: string; x: number; y: number; moveu: boolean } | null = null;
  /**
   * Posições que a pessoa mudou arrastando, em coordenadas do GRAFO — assim sobrevivem a zoom e pan.
   * Zeram quando o grafo muda (outra profundidade/layout), como no Dossiê, que remonta o gráfico.
   */
  protected readonly posicoesMovidas = signal<ReadonlyMap<string, { x: number; y: number }>>(
    new Map()
  );
  protected readonly arrastandoNo = signal(false);
  private medidor: CanvasRenderingContext2D | null = null;

  constructor() {
    afterNextRender(() => {
      const el = this.host()?.nativeElement;
      if (!el) return;
      const medir = () => {
        this.largura.set(el.clientWidth);
        this.alturaPx.set(el.clientHeight);
      };
      medir();
      const obs = new ResizeObserver(medir);
      obs.observe(el);
      this.destroyRef.onDestroy(() => obs.disconnect());
    });

    // Grafo novo (troca de profundidade/layout) ou área redimensionada = enquadra de novo e fecha
    // o popover, como o `zoomToFit` + `onZoom` do Dossiê.
    effect(() => {
      const nos = this.grafo().nos;
      const w = this.largura();
      const h = this.alturaPx();
      untracked(() => {
        this.transf.set(ajustarAoQuadro(nos, w, h));
        this.selecao.set(null);
        this.emFoco.set(null);
      });
    });

    // Grafo novo = posições originais (o arrasto vale para o desenho que está na tela).
    effect(() => {
      this.grafo();
      untracked(() => this.posicoesMovidas.set(new Map()));
    });
  }

  private medirTexto(texto: string): number {
    if (!this.noNavegador) return texto.length * 6.6;
    if (!this.medidor) {
      this.medidor = document.createElement('canvas').getContext('2d');
      if (this.medidor) this.medidor.font = `500 ${FONTE_ROTULO}px Montserrat, sans-serif`;
    }
    return this.medidor ? this.medidor.measureText(texto).width : texto.length * 6.6;
  }

  /**
   * O que depende SÓ do grafo (rótulo cortado, largura medida, cor, raio). Separado de `nosTela`
   * porque aquele recalcula a cada `pointermove`/`wheel`, e medir texto de 120 nós por quadro pesa.
   */
  private readonly nosFixos = computed(() =>
    this.grafo().nos.map(no => {
      const rotulo = truncarRotulo(String(no.rotulo ?? ''));
      return {
        no,
        r: raioDoNo(no, this.display.highlightRoot()),
        cor: corDoNo(no, this.display.highlightRoot()),
        alerta: emAlerta(no),
        rotulo,
        larguraRotulo: this.medirTexto(rotulo),
      };
    })
  );

  protected readonly nosTela = computed<NoTela[]>(() => {
    const { k, tx, ty } = this.transf();
    const movidas = this.posicoesMovidas();
    return this.nosFixos().map(f => {
      const p = movidas.get(f.no.id);
      return {
        ...f,
        sx: (p ? p.x : (f.no.x ?? 0)) * k + tx,
        sy: (p ? p.y : (f.no.y ?? 0)) * k + ty,
      };
    });
  });

  private readonly porId = computed(() => new Map(this.nosTela().map(n => [n.no.id, n])));

  protected readonly arestasTela = computed<ArestaTela[]>(() => {
    const pos = this.porId();
    const k = this.transf().k;
    const comSeta = this.grafo().layout !== null && this.grafo().layout !== undefined;
    const out: ArestaTela[] = [];
    this.grafo().arestas.forEach((a, i) => {
      const o = pos.get(a.origem);
      const d = pos.get(a.destino);
      if (!o || !d) return;
      out.push({
        chave: `${a.origem}>${a.destino}#${i}`,
        x1: o.sx,
        y1: o.sy,
        x2: d.sx,
        y2: d.sy,
        seta: comSeta ? pontaDaSeta(o.sx, o.sy, d.sx, d.sy, k) : null,
      });
    });
    return out;
  });

  protected readonly popover = computed(() => {
    const s = this.selecao();
    return s ? (this.porId().get(s.id) ?? null) : null;
  });

  protected readonly tooltip = computed(() => {
    const id = this.emFoco();
    return id ? (this.porId().get(id) ?? null) : null;
  });

  protected tipoRotulo(no: NoGrafoResponse): string {
    if (no.raiz) return 'Empresa consultada';
    return no.tipo === 'empresa' ? 'Empresa ligada' : 'Pessoa';
  }

  protected areaDeToque(n: NoTela): number {
    // O Dossiê pinta uma área de 14 unidades do grafo; limitada para não engolir os vizinhos.
    return Math.max(n.r + 3, Math.min(14 * this.transf().k, 18));
  }

  // ───────────────────────────── interação ─────────────────────────────

  protected entrarNo(n: NoTela): void {
    this.emFoco.set(n.no.id);
    if (!this.selecao()?.fixado) this.selecao.set({ id: n.no.id, fixado: false });
  }

  protected sairNo(): void {
    this.emFoco.set(null);
    if (!this.selecao()?.fixado) this.selecao.set(null);
  }

  protected clicarNo(n: NoTela, ev: Event): void {
    ev.stopPropagation();
    this.selecao.set({ id: n.no.id, fixado: true });
  }

  protected fecharPopover(): void {
    this.selecao.set(null);
  }

  protected aoRolar(ev: WheelEvent): void {
    ev.preventDefault();
    const el = this.host()?.nativeElement;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const fator = Math.pow(2, -ev.deltaY * 0.002);
    this.transf.set(zoomEm(this.transf(), fator, ev.clientX - r.left, ev.clientY - r.top));
    this.selecao.set(null);
  }

  protected iniciarArrasto(ev: PointerEvent): void {
    this.arrasto = { x: ev.clientX, y: ev.clientY, t: this.transf(), moveu: false };
    (ev.currentTarget as Element).setPointerCapture?.(ev.pointerId);
  }

  /**
   * Segurar a bolinha e arrastar move o nó (as arestas acompanham). O ponteiro é capturado pelo
   * SVG, então o movimento e o soltar chegam em `arrastar`/`soltar` mesmo fora do círculo.
   */
  protected iniciarArrastoNo(n: NoTela, ev: PointerEvent): void {
    ev.stopPropagation();
    if (ev.button !== 0) return;
    this.arrastoNo = { id: n.no.id, x: ev.clientX, y: ev.clientY, moveu: false };
    const svg = (ev.currentTarget as Element).closest('svg');
    svg?.setPointerCapture?.(ev.pointerId);
  }

  private moverNo(ev: PointerEvent): void {
    const a = this.arrastoNo!;
    if (!a.moveu && Math.hypot(ev.clientX - a.x, ev.clientY - a.y) < 3) return;
    if (!a.moveu) {
      a.moveu = true;
      this.arrastandoNo.set(true);
      this.emFoco.set(null);
    }
    const el = this.host()?.nativeElement;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const { k, tx, ty } = this.transf();
    const gx = (ev.clientX - r.left - tx) / k;
    const gy = (ev.clientY - r.top - ty) / k;
    this.posicoesMovidas.update(m => new Map(m).set(a.id, { x: gx, y: gy }));
  }

  protected arrastar(ev: PointerEvent): void {
    if (this.arrastoNo) {
      this.moverNo(ev);
      return;
    }
    const a = this.arrasto;
    if (!a) return;
    const dx = ev.clientX - a.x;
    const dy = ev.clientY - a.y;
    if (!a.moveu && Math.hypot(dx, dy) < 3) return;
    a.moveu = true;
    this.transf.set({ k: a.t.k, tx: a.t.tx + dx, ty: a.t.ty + dy });
  }

  protected soltar(): void {
    if (this.arrastoNo) {
      // Soltou sem mover = clique no nó: fixa o popover (com a captura no SVG, o `click` do
      // círculo não chega). Depois de arrastar, o popover acompanha o nó onde ele parou.
      const { id, moveu } = this.arrastoNo;
      if (!moveu) this.selecao.set({ id, fixado: true });
      this.arrastoNo = null;
      this.arrastandoNo.set(false);
      return;
    }
    // Clique sem arrasto no fundo fecha o popover (o `onBackgroundClick` do Dossiê).
    if (this.arrasto && !this.arrasto.moveu) this.selecao.set(null);
    this.arrasto = null;
  }

  @HostListener('document:keydown.escape')
  protected aoEsc(): void {
    this.selecao.set(null);
  }
}
